import React, { useState } from "react";
import { Menu, X, ArrowRight, Users, Target, BarChart3, Shield } from "lucide-react";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/home" className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className={`text-sm font-semibold transition-colors ${
                    link.name === "About"
                      ? "text-blue-600"
                      : "text-slate-600 hover:text-blue-600"
                  }`}
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "About"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">
            Log In
          </a>
        </div>

        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[460px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-bold ${
                  link.name === "About" ? "text-blue-600" : "text-slate-900"
                }`}
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="pt-36 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
            About Us
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Our Mission Is to Remove Guesswork from Athlete Development
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed max-w-4xl">
            Nuvio helps coaches define a clear standard, helps athletes understand exactly what is
            required, and turns that standard into daily execution. We built this platform so every
            rep has purpose and every program can track real progress.
          </p>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <Target className="text-blue-600 mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Clarity</h2>
            <p className="text-slate-600">
              Every athlete gets clear, coach-aligned development priorities.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <BarChart3 className="text-blue-600 mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Accountability</h2>
            <p className="text-slate-600">
              Coaches and athletes can measure consistency, completion, and growth.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <Shield className="text-blue-600 mb-3" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Confidence</h2>
            <p className="text-slate-600">
              Programs enter the season knowing players trained the right way.
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto rounded-2xl bg-slate-900 text-white p-8 md:p-10">
          <h3 className="text-2xl md:text-3xl font-extrabold mb-3 flex items-center gap-2">
            <Users className="text-blue-400" />
            Built for Programs That Want Sustainable Growth
          </h3>
          <p className="text-slate-300 mb-6">
            We believe strong cultures are built on standards, not speeches. Nuvio gives programs
            the structure to develop better habits and better outcomes year after year.
          </p>
          <a
            href="/create-account"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/20"
          >
            Start With Nuvio <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}
